import office from 'src/services/office';
import { areStringsInsensitivelyEqual, pick, } from 'shared/utils/utils';
import { RangeAddress } from 'shared/excel/rangeAddress';


const Data = {
  salesforceParamKey: 'sf-metadata',
  salesforceWorksheetPattern: 'SalesforcePicklists',
  rangeParameters: ['address', 'rowIndex', 'columnIndex', 'rowCount', 'columnCount'],
  Header: {
    rowCount: 2,
    columnCount: 3,
  },
  currentVersion: 1,
};


class MetadataHandler {
  usersData = null;
  context = null;

  async getPicklistRanges(userName, objectName, rawField) {
    const { userData, worksheet } = await this.getUserData(userName);
    const { data: { valuesRange, labelsRange }, wasUpdated } =
      await this.updatePicklistData(worksheet, userData, objectName, rawField);

    if (wasUpdated) {
      await this.saveUsersData();
    }

    return { valuesRange, labelsRange };
  }

  async loadUsersData() {
    if (!this.usersData) {
      this.usersData = await office.getParam(Data.salesforceParamKey) ?? {};
      // console.log('MetadataHandler::loadUsersData: ', this.usersData);
    }
  }

  async saveUsersData() {
    const data = Object.entries(this.usersData).reduce((out, [key, value]) =>
      Object.assign(out, { [key]: {
        ...value,
        picklists: {
          ...value.picklists,
          records: value.picklists.records.map(x => pick(x, ['objectName', 'picklistName'])),
        }
      }})
    , {});
    // console.log('MetadataHandler::saveUsersData: ', data);
    await office.setParam(Data.salesforceParamKey, data);
  }

  clearUserData() {
    this.usersData = null;
  }

  async getUserData(userName) {
    await this.loadUsersData();

    const { context } = this;

    let worksheet = null;
    let userData = this.usersData[userName];

    if (userData) {
      const { worksheetName } = userData.picklists;
      worksheet = context.workbook.worksheets.getItemOrNullObject(worksheetName);
      await context.sync();
      if (worksheet.isNullObject) {
        userData = null;
      }
    }

    if (!userData) {
      const name = await office.getWorksheetName(Data.salesforceWorksheetPattern);
      worksheet = context.workbook.worksheets.add(name);
      worksheet.visibility = Excel.SheetVisibility.veryHidden;

      userData = { picklists: { worksheetName: name, records: [], version: Data.currentVersion }};
      this.usersData[userName] = userData;
      await this.saveUsersData();
    }

    return { worksheet, userData };
  }

  async updatePicklistData(worksheet, userData, objectName, rawField) {
    const { records, worksheetName } = userData.picklists;
    const { name: picklistName } = rawField;
    let picklistIndex = records.findIndex(x => areStringsInsensitivelyEqual(objectName, x.objectName)
      && areStringsInsensitivelyEqual(picklistName, x.picklistName)
    );

    if (picklistIndex === -1) {
      records.push({ picklistName, objectName });
      picklistIndex = records.length - 1;
    }

    const data = records[picklistIndex];
    // TODO: we need to compare content of picklist instead of simply checking valuesRange
    // Or we can simply override the current values/lables as it is done now
    // if (data.valuesRange) {
    //   return { wasUpdated: false, data };
    // }

    const rowIndex = picklistIndex * Data.Header.rowCount;
    const valuesCount = await this.writePicklistData(worksheet, rowIndex, objectName, rawField);

    const valuesRange = new RangeAddress({
      sheet: worksheetName, rowIndex, columnIndex: Data.Header.columnCount, rowCount: 1, columnCount: valuesCount,
    });
    const labelsRange = valuesRange.getOffsetRange(1, 0);

    Object.assign(data, { valuesRange, labelsRange });

    return { wasUpdated: true, data };
  }

  async writePicklistData(worksheet, rowIndex, objectName, rawField) {
    const data = rawField.picklistValues.reduce((out, x) => {
      if (x.active) {
        out.values.push(x.value);
        out.labels.push(x.label);
      }
      return out;
    }, { values: [], labels: []});

    const valuesCount = data.values.length;
    const header = [objectName, rawField.name, data.values.length];

    worksheet.getRangeByIndexes(
      rowIndex, 0, Data.Header.rowCount, header.length + valuesCount
    ).values = [
      [...header, ...data.values.map(value => value?.length > 0 ? `'${value}` : value)],
      [...header, ...data.labels]
    ];
    await this.context.sync();

    return valuesCount;
  }
};


export const metadataHandler = new MetadataHandler();
export default metadataHandler;
